import { coreAppStore } from '@stores/utility-stores/project';
import { Tenant } from '@vincerosoft/assessfluence-types';
import { type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { firebaseConfig, isLocal } from './env';

let appCheckInitialized = false;
let initializedFirestoreDbId: string | null = null;

const FIRESTORE_CACHE_SCHEMA_VERSION = 'firebase-12.19-cache-reset-1';
const FIRESTORE_CACHE_SCHEMA_KEY = 'firestoreCacheSchemaVersion';
let staleFirestoreCacheCleared = false;

async function clearStaleFirestoreCache(): Promise<boolean> {
  if (staleFirestoreCacheCleared) {
    return true;
  }
  staleFirestoreCacheCleared = true;

  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') {
    return false;
  }

  if (window.localStorage.getItem(FIRESTORE_CACHE_SCHEMA_KEY) === FIRESTORE_CACHE_SCHEMA_VERSION) {
    return true;
  }

  try {
    const databases = await indexedDB.databases();
    for (const database of databases) {
      if (!database.name?.startsWith('firestore/')) {
        continue;
      }

      const deleted = await new Promise<boolean>((resolve) => {
        const request = indexedDB.deleteDatabase(database.name!);
        request.onsuccess = () => resolve(true);
        request.onerror = () => resolve(false);
        request.onblocked = () => resolve(false);
      });

      if (!deleted) {
        return false;
      }
    }

    window.localStorage.setItem(FIRESTORE_CACHE_SCHEMA_KEY, FIRESTORE_CACHE_SCHEMA_VERSION);
    return true;
  } catch {
    return false;
  }
}

export async function initializeFirebaseServices(
  tenant: Tenant | null,
  app: FirebaseApp | null
): Promise<void> {
  const { setDb, setStorage, setFunctions, setApp, setAuth, setIsInitialized } =
    coreAppStore.getState();

  if (!app) {
    console.log('No Firebase app provided. Skipping Firebase service initialization.');
    return;
  }

  const firestoreCacheReady = await clearStaleFirestoreCache();

  const tenantDependantServices = () => {
    if (!tenant) {
      console.log('No tenant provided. Skipping tenant-dependent Firebase service initialization.');
      return;
    }
    const auth = getAuth(app);
    auth.tenantId = tenant.tenant_id!;
    setAuth(auth);

    const databaseId = tenant.project_id!;

    // Firestore doesn't allow re-initialization with different settings, so we need to check if we've already initialized it for the given databaseId
    const db =
      initializedFirestoreDbId === databaseId
        ? getFirestore(app, databaseId)
        : initializeFirestore(
            app,
            {
              ignoreUndefinedProperties: true,
              localCache: firestoreCacheReady
                ? persistentLocalCache({
                    tabManager: persistentMultipleTabManager()
                  })
                : memoryLocalCache()
            },
            databaseId
          );
    initializedFirestoreDbId = databaseId;

    const bucket = tenant?.bucket || firebaseConfig.storageBucket;
    const normalizedBucket = bucket?.replace(/^gs:\/\//, '');
    const storage = normalizedBucket
      ? getStorage(app, `gs://${normalizedBucket}`)
      : getStorage(app);

    const functions = getFunctions(app, 'europe-west1');

    setDb(db);
    setStorage(storage);
    setFunctions(functions);
  };

  tenantDependantServices();

  if (isLocal) {
    (
      self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = firebaseConfig.appCheckDebugKey || true;
  }

  if (firebaseConfig.recaptchaKey && !appCheckInitialized) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(firebaseConfig.recaptchaKey),
      isTokenAutoRefreshEnabled: true
    });
    appCheckInitialized = true;
  }

  setApp(app);

  setIsInitialized(true);
}
